import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
    ChevronDown,
    Download,
    Edit3,
    Eye,
    FileText,
    RefreshCw,
    Search,
    Send,
    Share2,
    ShieldCheck,
    Trash2,
    Users,
    X,
} from 'lucide-react';
import { getUserRole, isAuthenticated } from '../../../../src/utils/auth';
import Sidebar from '../../../../src/components/Sidebar/sidebar';
import Header from '../../../../src/components/Header';
import Pagination from '../../../../src/components/Pagination/Pagination';
import '../Retail/convertedRetailPolicy.css';
import api from "../../../utils/api";
import { API_BASE_URL } from '../../../utils/apiBase';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CONVERTED_POLICY_PRIVILEGES } from '../../../utils/convertedPolicyPrivileges';
import PolicyDocumentDropzone from '../PolicyDocumentDropzone';

const ConvertedCorporatePolicy = () => {
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [policies, setPolicies] = useState([]);
    const [endorsements, setEndorsements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPolicy, setEditingPolicy] = useState({});
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEndorsementViewModal, setShowEndorsementViewModal] = useState(false);
    const [viewingPolicy, setViewingPolicy] = useState({});
    const [viewingEndorsement, setViewingEndorsement] = useState({});
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [selectedPolicyId, setSelectedPolicyId] = useState(null);
    const [confirmationId, setConfirmationId] = useState('');
    const [expectedPolicyNumber, setExpectedPolicyNumber] = useState('');

    const [searchTerm, setSearchTerm] = useState('');
    const [searchColumn, setSearchColumn] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [policyTypeFilter, setPolicyTypeFilter] = useState('all');
    const [insurerFilter, setInsurerFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [ownerFilter, setOwnerFilter] = useState('all');
    const [renewalStatusFilter, setRenewalStatusFilter] = useState('all');
    const [selectedRows, setSelectedRows] = useState([]);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [insurerInput, setInsurerInput] = useState('');
    const [insurerOptions, setInsurerOptions] = useState([]);
    const [branches, setBranches] = useState([]);
    const [classOfPolicyOptions, setClassOfPolicyOptions] = useState([]);
    const [policyNames, setPolicyNames] = useState([]);

    // Client name suggestion states
    const [clientInput, setClientInput] = useState('');

    const [selectedFile, setSelectedFile] = useState(null);
    const [policyStatusMap, setPolicyStatusMap] = useState({});
    const [showPolicyUploadModal, setShowPolicyUploadModal] = useState(false);
    const [uploadTargetPolicy, setUploadTargetPolicy] = useState(null);
    const [statusUploadFile, setStatusUploadFile] = useState(null);
    const privileges = JSON.parse(localStorage.getItem("privileges") || "[]");
    const isAdmin = getUserRole() === "admin";

    const corporatePrivileges = CONVERTED_POLICY_PRIVILEGES.corporate;
    const hasPrivilege = (key) => isAdmin || privileges.includes(key);
    const permissions = {
        downloadSelected: hasPrivilege(corporatePrivileges.downloadSelected),
        view: hasPrivilege(corporatePrivileges.view),
        edit: hasPrivilege(corporatePrivileges.edit),
        renew: hasPrivilege(corporatePrivileges.renew),
        downloadDocument: hasPrivilege(corporatePrivileges.downloadDocument),
        share: hasPrivilege(corporatePrivileges.share),
        delete: hasPrivilege(corporatePrivileges.delete)
    };



    // Debounced values for calculations
    const [debouncedValues, setDebouncedValues] = useState({
        grossPremium: '',
        gstPercentage: '',
        brokeragePercentage: '',
    });

    // Helper function to format date to YYYY-MM-DD for input type="date"
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to format date to dd-mm-yyyy for display
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    useEffect(() => {
        fetchPolicies();
        fetchEndorsements();
        fetchInsurerOptions();
        fetchClassOfPolicies();
    }, []);

    useEffect(() => {
        if (showEditModal || showViewModal || showDeleteConfirmationModal || showEndorsementViewModal || showPolicyUploadModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [showEditModal, showViewModal, showDeleteConfirmationModal, showEndorsementViewModal, showPolicyUploadModal]);

    const fetchPolicies = async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/add-policy/corporate-submitted-policies`);
            setPolicies(res.data);
        } catch (err) {
            console.error('Failed to fetch policies', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchEndorsements = async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/endorsements`);
            const normalized = (res.data || [])
                .filter((e) => e.clientType === "Corporate")
                .map((e) => ({
                    _id: e._id,
                    clientName: e.policyId?.clientName || '',
                    insurerName: e.policyId?.insurerName || '',
                    policyName: e.policyId?.policyName || '',
                    policyNumber: e.endorsementNumber || '',
                    policyStartDate: e.endorsementDate || e.policyId?.policyStartDate || '',
                    policyEndDate: e.policyId?.policyEndDate || '',
                    grossPremium: e.additionalGrossPremium ?? '',
                    netPremium: e.additionalNetPremium ?? '',
                    isEndorsement: true
                }));

            setEndorsements(normalized);
        } catch (err) {
            console.error('Failed to fetch endorsements', err);
        }
    };

    const ensureOption = (options, value, key = 'insurerName') => {
        if (!value) return options;
        const exists = options.some((item) =>
            String(item?.[key] || item || '').toLowerCase() === String(value).toLowerCase()
        );
        return exists ? options : [{ [key]: value, _id: value }, ...options];
    };

    const ensureStringOption = (options, value) => {
        if (!value) return options;
        return options.some((item) => String(item).toLowerCase() === String(value).toLowerCase())
            ? options
            : [value, ...options];
    };

    const fetchInsurerOptions = async (query = '') => {
        try {
            const res = await api.get(
                `${API_BASE_URL}/api/add-policy/insurer-search?query=${encodeURIComponent(query)}`
            );
            setInsurerOptions(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to fetch insurer options', err);
            setInsurerOptions([]);
        }
    };

    const fetchBranches = async (insurerName) => {
        if (!insurerName) {
            setBranches([]);
            return;
        }

        try {
            const res = await api.get(
                `${API_BASE_URL}/api/add-policy/branches?insurerName=${encodeURIComponent(insurerName)}`
            );
            setBranches(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Error fetching branches:', err);
            setBranches([]);
        }
    };

    const fetchClassOfPolicies = async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/add-policy/class-of-policies`);
            setClassOfPolicyOptions(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Error loading class of policies:', err);
            setClassOfPolicyOptions([]);
        }
    };

    const fetchPolicyNames = async (classOfPolicyId) => {
        if (!classOfPolicyId) {
            setPolicyNames([]);
            return;
        }

        try {
            const res = await api.get(
                `${API_BASE_URL}/api/add-policy/policy-names?classOfPolicyId=${encodeURIComponent(classOfPolicyId)}`
            );
            setPolicyNames(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Error loading policy names:', err);
            setPolicyNames([]);
        }
    };

    const handleEditClick = (policy) => {
        const classOfPolicyId = typeof policy.classOfPolicyId === 'object'
            ? policy.classOfPolicyId?._id
            : policy.classOfPolicyId;
        const baseBrokeragePercentage = policy.baseBrokeragePercentage ?? policy.brokeragePercentage ?? '';

        // Format dates for display in input fields when setting editingPolicy
        setEditingPolicy({
            ...policy,
            classOfPolicyId: classOfPolicyId || '',
            baseBrokeragePercentage,
            commissionAdjustmentType: policy.commissionAdjustmentType || 'add',
            commissionAdjustmentPercentage: policy.commissionAdjustmentPercentage ?? '',
            transactionDate: formatDateForInput(policy.transactionDate),
            policyIssueDate: formatDateForInput(policy.policyIssueDate),
            policyStartDate: formatDateForInput(policy.policyStartDate),
            policyEndDate: formatDateForInput(policy.policyEndDate),
        });
        setInsurerInput(policy.insurerName || '');
        setClientInput(policy.clientName || ''); // Set initial client name
        setInsurerOptions((current) => ensureOption(current, policy.insurerName));
        fetchBranches(policy.insurerName || '');
        fetchPolicyNames(classOfPolicyId || '');
        setShowEditModal(true);
    };

    const calculateAdjustedBrokeragePercentage = (baseValue, adjustmentType, adjustmentValue) => {
        const baseRate = parseFloat(baseValue);
        const adjustmentRate = parseFloat(adjustmentValue);

        if (Number.isNaN(baseRate)) return '';
        if (Number.isNaN(adjustmentRate)) return baseRate.toFixed(2);

        const adjustedRate = adjustmentType === 'subtract'
            ? baseRate - adjustmentRate
            : baseRate + adjustmentRate;

        return Math.max(adjustedRate, 0).toFixed(2);
    };

    const handlePolicyNumberClick = (policy) => {
        if (policy.isEndorsement) return;
        setViewingPolicy({
            ...policy,
            transactionDate: formatDateForInput(policy.transactionDate),
            policyIssueDate: formatDateForInput(policy.policyIssueDate),
            policyStartDate: formatDateForInput(policy.policyStartDate),
            policyEndDate: formatDateForInput(policy.policyEndDate),
        });
        setShowViewModal(true);
    };

    const handleEndorsementView = async (endorsementRow) => {
        try {
            const res = await api.get(
                `${API_BASE_URL}/api/endorsements/view/${endorsementRow._id}`
            );

            setViewingEndorsement({
                ...res.data,
                clientName: endorsementRow.clientName,
                insurerName: endorsementRow.insurerName,
                policyName: endorsementRow.policyName,
                policyNumber: endorsementRow.policyNumber,
                policyStartDate: endorsementRow.policyStartDate,
                policyEndDate: endorsementRow.policyEndDate
            });
            setShowEndorsementViewModal(true);
        } catch (err) {
            console.error('Failed to fetch endorsement details', err);
            alert('Failed to load endorsement details.');
        }
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;

        setEditingPolicy(prev => {
            const updatedPolicy = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };

            // Trigger the calculation whenever relevant fields are updated
            if (name === "grossPremium" || name === "gstPercentage" || name === "brokeragePercentage") {
                setDebouncedValues(prevValues => ({
                    ...prevValues,
                    [name]: value
                }));
            }

            return updatedPolicy;
        });
    };

    const handleInsurerChange = (e) => {
        const insurerName = e.target.value;
        setInsurerInput(insurerName);
        setEditingPolicy(prev => ({
            ...prev,
            insurerName,
            branchName: ''
        }));
        fetchBranches(insurerName);
    };

    const handleClassOfPolicyChange = (e) => {
        const classOfPolicyId = e.target.value;
        const selectedClass = classOfPolicyOptions.find(
            item => String(item._id) === String(classOfPolicyId)
        );

        setEditingPolicy(prev => ({
            ...prev,
            classOfPolicyId,
            classOfPolicyName: selectedClass?.classOfPolicyName || '',
            policyName: '',
            gstPercentage: '',
            baseBrokeragePercentage: '',
            commissionAdjustmentType: 'add',
            commissionAdjustmentPercentage: '',
            brokeragePercentage: '',
            totalBrokerage: ''
        }));
        setPolicyNames([]);
        fetchPolicyNames(classOfPolicyId);
    };

    const handlePolicyNameChange = async (e) => {
        const selectedPolicyName = e.target.value;
        setEditingPolicy(prev => ({ ...prev, policyName: selectedPolicyName }));

        if (selectedPolicyName) {
            try {
                const res = await api.get(
                    `${API_BASE_URL}/api/add-policy/policy-details?policyName=${encodeURIComponent(selectedPolicyName)}`
                );
                setEditingPolicy(prev => ({
                    ...prev,
                    gstPercentage: res.data.defaultGST,
                    baseBrokeragePercentage: res.data.defaultBrokeragePercentage,
                    brokeragePercentage: calculateAdjustedBrokeragePercentage(
                        res.data.defaultBrokeragePercentage,
                        prev.commissionAdjustmentType,
                        prev.commissionAdjustmentPercentage
                    )
                }));
                // Update debounced values immediately after fetching
                setDebouncedValues({
                    ...debouncedValues,
                    gstPercentage: res.data.defaultGST,
                    brokeragePercentage: res.data.defaultBrokeragePercentage
                });
            } catch (err) {
                console.error('Error fetching policy details:', err);
            }
        }
    };

    useEffect(() => {
        if (!showEditModal) return;

        const adjustedBrokerage = calculateAdjustedBrokeragePercentage(
            editingPolicy.baseBrokeragePercentage,
            editingPolicy.commissionAdjustmentType,
            editingPolicy.commissionAdjustmentPercentage
        );

        setEditingPolicy(prev => ({
            ...prev,
            brokeragePercentage: adjustedBrokerage
        }));
    }, [
        showEditModal,
        editingPolicy.baseBrokeragePercentage,
        editingPolicy.commissionAdjustmentType,
        editingPolicy.commissionAdjustmentPercentage
    ]);

    const calculateAmounts = useCallback(() => {
        const grossPremium = parseFloat(editingPolicy.grossPremium);
        const gstPercentage = parseFloat(editingPolicy.gstPercentage);
        const brokeragePercentage = parseFloat(editingPolicy.brokeragePercentage);

        if (
            isNaN(grossPremium) ||
            isNaN(gstPercentage) ||
            gstPercentage < 0
        ) {
            return;
        }

        // ✅ GST-inclusive calculation
        const netPremium = (grossPremium * 100) / (100 + gstPercentage);
        const gstAmount = grossPremium - netPremium;

        let totalBrokerage = 0;
        if (!isNaN(brokeragePercentage) && brokeragePercentage >= 0) {
            totalBrokerage = (netPremium * brokeragePercentage) / 100;
        }

        setEditingPolicy(prev => ({
            ...prev,
            netPremium: netPremium.toFixed(2),
            gstAmount: gstAmount.toFixed(2),
            totalBrokerage: totalBrokerage.toFixed(2)
        }));
    }, [
        editingPolicy.grossPremium,
        editingPolicy.gstPercentage,
        editingPolicy.brokeragePercentage
    ]);

    // Debounce effect for calculations
    useEffect(() => {
        const timer = setTimeout(() => {
            calculateAmounts();
        }, 500); // Adjust time as needed

        return () => clearTimeout(timer); // Clear the previous timer on each change
    }, [debouncedValues, calculateAmounts]);

    // Recalculate amounts whenever relevant editingPolicy fields or debouncedValues change
    useEffect(() => {
        calculateAmounts();
    }, [calculateAmounts]);

    useEffect(() => {
        if (!showEditModal || editingPolicy.classOfPolicyId || !editingPolicy.classOfPolicyName || classOfPolicyOptions.length === 0) {
            return;
        }

        const selectedClass = classOfPolicyOptions.find(
            item => String(item.classOfPolicyName || '').toLowerCase() === String(editingPolicy.classOfPolicyName || '').toLowerCase()
        );

        if (selectedClass) {
            setEditingPolicy(prev => ({
                ...prev,
                classOfPolicyId: selectedClass._id || '',
                classOfPolicyName: selectedClass.classOfPolicyName || prev.classOfPolicyName
            }));
            fetchPolicyNames(selectedClass._id || '');
        }
    }, [showEditModal, editingPolicy.classOfPolicyId, editingPolicy.classOfPolicyName, classOfPolicyOptions]);


    const handleEditSave = async (e) => {
        e.preventDefault();

        const formData = new FormData();
        Object.entries(editingPolicy).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                return;
            }

            if (typeof value === 'object' && !(value instanceof File)) {
                return;
            }

            formData.append(key, value);
        });

        if (selectedFile) {
            formData.append('policyDoc', selectedFile);
        }

        try {
            await api.put(
                `${API_BASE_URL}/api/add-policy/${editingPolicy._id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setShowEditModal(false);
            fetchPolicies();
        } catch (err) {
            console.error('Error updating policy', err);
            alert('Failed to update policy. Please try again.');
        }
    };


    const handleDeleteClick = (policy) => {
        setSelectedPolicyId(policy._id);
        setConfirmationId('');
        setExpectedPolicyNumber(policy.policyNumber);
        setShowDeleteConfirmationModal(true);
    };

    const handleDelete = async () => {
        if (confirmationId !== expectedPolicyNumber) {
            alert('Policy Number does not match.');
            return;
        }

        try {
            await api.delete(`${API_BASE_URL}/api/add-policy/${selectedPolicyId}`);
            setShowDeleteConfirmationModal(false);
            setConfirmationId('');
            fetchPolicies();
        } catch (err) {
            console.error('Error deleting policy', err);
            alert(err.response?.data?.error || 'Failed to delete policy. Please try again.');
        }
    };

    const handleCloseDeleteConfirmationModal = () => {
        setShowDeleteConfirmationModal(false);
        setConfirmationId('');
    };

    const deriveInitialPolicyStatus = (policy) => {
        if (policy?.policyStatus === 'policy_doc_uploaded') return 'policy_doc_uploaded';
        if (policy?.policyStatus === 'policy_not_uploaded') return 'policy_not_uploaded';
        if (policy?.policyStatus === 'doc_sent_to_client') return 'doc_sent_to_client';

        const rawStatus = String(
            policy?.policyStatus || policy?.documentStatus || policy?.status || ''
        ).toLowerCase();

        if (rawStatus === 'doc_sent_to_client' || rawStatus === 'sent_to_client' || rawStatus.includes('sent')) {
            return 'doc_sent_to_client';
        }
        if (
            rawStatus === 'policy_doc_uploaded' ||
            rawStatus === 'ready_to_send_client' ||
            rawStatus.includes('uploaded') ||
            rawStatus.includes('ready') ||
            rawStatus.includes('received')
        ) {
            return 'policy_doc_uploaded';
        }
        if (policy?.policyDocPath) {
            return 'policy_doc_uploaded';
        }
        if (rawStatus === 'policy_not_uploaded' || rawStatus.includes('pending') || rawStatus.includes('not uploaded')) {
            return 'policy_not_uploaded';
        }
        return 'policy_not_uploaded';
    };

    const getPolicyStatus = useCallback((policy) => {
        if (!policy?._id) return 'policy_not_uploaded';
        return policyStatusMap[policy._id] || deriveInitialPolicyStatus(policy);
    }, [policyStatusMap]);

    const handleOpenPolicyUploadModal = (policy) => {
        setUploadTargetPolicy(policy);
        setStatusUploadFile(null);
        setShowPolicyUploadModal(true);
    };

    const handlePolicyUploadFromStatus = async () => {
        if (!uploadTargetPolicy?._id) return;
        if (!statusUploadFile) {
            alert('Please choose a policy document before uploading.');
            return;
        }

        const formData = new FormData();
        formData.append('policyDoc', statusUploadFile);
        formData.append('policyStatus', 'Policy Doc Uploaded');

        try {
            await api.put(
                `${API_BASE_URL}/api/add-policy/${uploadTargetPolicy._id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setPolicyStatusMap((prev) => ({
                ...prev,
                [uploadTargetPolicy._id]: 'policy_doc_uploaded',
            }));
            setShowPolicyUploadModal(false);
            setUploadTargetPolicy(null);
            setStatusUploadFile(null);
            fetchPolicies();
        } catch (err) {
            console.error('Error uploading policy document from status modal', err);
            alert('Failed to upload policy document. Please try again.');
        }
    };

    const handlePolicyStatusChange = async (policy, statusValue) => {
        if (!policy?._id || policy.isEndorsement) return;
        const currentStatus = getPolicyStatus(policy);

        if (statusValue === currentStatus) return;

        if (statusValue === 'share_to_client_mail') {
            if (currentStatus === 'policy_not_uploaded') {
                alert('Upload the policy document before sharing to client.');
                return;
            }

            const formData = new FormData();
            formData.append('policyStatus', 'Share to Client (Mail)');

            try {
                await api.put(
                    `${API_BASE_URL}/api/add-policy/${policy._id}`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
                setPolicyStatusMap((prev) => ({
                    ...prev,
                    [policy._id]: 'doc_sent_to_client',
                }));
                fetchPolicies();
            } catch (err) {
                console.error('Error sharing policy document to client', err);
                alert(err?.response?.data?.error || 'Failed to share policy document. Please try again.');
            }
            return;
        }

        if (statusValue === 'policy_doc_uploaded') {
            if (currentStatus === 'doc_sent_to_client') return;
            handleOpenPolicyUploadModal(policy);
            return;
        }

        if (currentStatus !== 'policy_not_uploaded' || policy?.policyDocPath) {
            alert('Policy document already uploaded. Status cannot be changed back to Policy Doc Pending.');
            return;
        }

        const formData = new FormData();
        formData.append('policyStatus', 'Policy Doc Pending');

        try {
            await api.put(
                `${API_BASE_URL}/api/add-policy/${policy._id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            setPolicyStatusMap((prev) => ({
                ...prev,
                [policy._id]: 'policy_not_uploaded',
            }));
        } catch (err) {
            console.error('Error updating policy status to pending', err);
            alert('Failed to update policy status. Please try again.');
        }
    };

    const getOwnerNames = useCallback((policy) => {
        const names = [
            policy?.owner?.name,
            policy?.owner?.username,
            policy?.ownerId?.name,
            policy?.ownerId?.username,
            policy?.ownerId?.reportingManager?.managerName,
            policy?.reportingManager?.managerName,
            policy?.assignedBy?.name,
            policy?.assignedBy?.username,
            policy?.createdBy?.name,
            policy?.createdBy?.username,
            policy?.createdBy?.userName,
            policy?.updatedBy?.name,
            policy?.updatedBy?.username,
            policy?.updatedBy?.userName,
            policy?.uploadedBy?.name,
            policy?.uploadedBy?.username,
            policy?.uploadedByName,
            policy?.policyDocUploadedBy?.name,
            policy?.policyDocUploadedBy?.username,
            policy?.policyDocUploadedByName,
            policy?.addedBy?.name,
            policy?.addedBy?.username,
            policy?.addedByName,
        ];

        return [...new Set(names.map((name) => String(name || '').trim()).filter(Boolean))];
    }, []);

    const getOwnerName = useCallback((policy) => getOwnerNames(policy)[0] || '', [getOwnerNames]);

    const getAssignedUserName = useCallback((policy) => {
        const ownerName = getOwnerName(policy);
        if (ownerName) return ownerName;
        if (policy?.createdBy?.name) return policy.createdBy.name;
        if (policy?.updatedBy?.userName) return policy.updatedBy.userName;
        return policy?.isEndorsement ? 'Endorsements' : 'Assigned';
    }, [getOwnerName]);

    const getRenewalStatus = useCallback((policy) => {
        if (policy?.isEndorsement) return 'Not Applicable';
        if (policy?.renewalTracking?.status) return policy.renewalTracking.status;
        return policy?.policyToBeRenewed ? 'Pending' : 'Not Required';
    }, []);

    const getPolicyType = useCallback((policy) => policy?.policyName || '', []);

    const getPolicyLifecycleStatus = useCallback((policy) => {
        if (policy?.isEndorsement) return 'Endorsement';
        if (getRenewalStatus(policy) === 'Renewed') return 'Renewed';

        const endDate = policy?.policyEndDate ? new Date(policy.policyEndDate) : null;
        if (!endDate || Number.isNaN(endDate.getTime())) return 'Pending';

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        return endDate < today ? 'Expired' : 'Active';
    }, [getRenewalStatus]);

    const getPolicyStatusLabel = useCallback((statusValue) => {
        const labels = {
            policy_not_uploaded: 'Policy Doc Pending',
            policy_doc_uploaded: 'Policy Doc Uploaded',
            doc_sent_to_client: 'Doc Sent to Client',
            share_to_client_mail: 'Share to Client',
        };
        return labels[statusValue] || statusValue || 'Pending';
    }, []);

    const formatCurrency = (value) => {
        const amount = Number(value || 0);
        if (!amount) return 'Rs. 0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(amount).replace('₹', 'Rs. ');
    };

    const getInitials = (name = '') => {
        const parts = String(name).trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return 'CP';
        return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
    };

    const getExpiryMeta = useCallback((policy) => {
        const endDate = policy?.policyEndDate ? new Date(policy.policyEndDate) : null;
        if (!endDate || Number.isNaN(endDate.getTime())) {
            return { label: 'No expiry', tone: 'muted', days: null };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        const days = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
        if (days < 0) return { label: `${Math.abs(days)} days overdue`, tone: 'danger', days };
        if (days <= 30) return { label: `${days} days left`, tone: 'warning', days };
        return { label: `${days} days left`, tone: 'success', days };
    }, []);

    const getPolicyFieldValue = useCallback((policy, columnKey) => {
        switch (columnKey) {
            case 'clientName':
                return policy.clientName || '';
            case 'insurerName':
                return policy.insurerName || '';
            case 'policyName':
                return policy.policyName || '';
            case 'policyNumber':
                return policy.policyNumber || '';
            case 'policyStartDate':
                return formatDateForDisplay(policy.policyStartDate) || '';
            case 'policyEndDate':
                return formatDateForDisplay(policy.policyEndDate) || '';
            case 'policyType':
                return getPolicyType(policy);
            case 'owner':
                return getOwnerNames(policy).join(', ');
            case 'renewalStatus':
                return getRenewalStatus(policy);
            case 'grossPremium':
                return policy.grossPremium ?? '';
            case 'netPremium':
                return policy.netPremium ?? '';
            default:
                return '';
        }
    }, [getOwnerNames, getPolicyType, getRenewalStatus]);

    const statusOptions = [
        'Active',
        'Expired',
        'Pending',
        'Renewed',
        'Policy Doc Uploaded',
        'Policy Doc Pending',
        'Doc Sent to Client',
    ];

    const combinedPolicies = useMemo(() => [...policies, ...endorsements], [policies, endorsements]);

    const filterOptions = useMemo(() => {
        const unique = (items) => [...new Set(items.filter(Boolean))].sort((a, b) => a.localeCompare(b));

        return {
            policyTypes: unique(combinedPolicies.map(getPolicyType)),
            insurers: unique(combinedPolicies.map((policy) => policy.insurerName || '')),
            owners: unique(combinedPolicies.flatMap(getOwnerNames)),
            renewalStatuses: unique(combinedPolicies.map(getRenewalStatus)),
        };
    }, [combinedPolicies, getOwnerNames, getPolicyType, getRenewalStatus]);

    const isWithinDateRange = useCallback((policy) => {
        if (!dateFrom && !dateTo) return true;

        const policyDate = policy.policyStartDate ? new Date(policy.policyStartDate) : null;
        if (!policyDate || Number.isNaN(policyDate.getTime())) return false;

        const fromDate = dateFrom ? new Date(dateFrom) : null;
        const toDate = dateTo ? new Date(dateTo) : null;

        if (fromDate && policyDate < fromDate) return false;
        if (toDate) {
            const endOfToDate = new Date(toDate);
            endOfToDate.setHours(23, 59, 59, 999);
            if (policyDate > endOfToDate) return false;
        }

        return true;
    }, [dateFrom, dateTo]);

    const filteredAndDatedPolicies = useMemo(() => {
        const normalizedSearchTerm = searchTerm.trim().toLowerCase();

        return combinedPolicies.filter((policy) => {
            const policyStatusLabel = getPolicyStatusLabel(getPolicyStatus(policy));
            const lifecycleStatus = getPolicyLifecycleStatus(policy);

            if (policyTypeFilter !== 'all' && getPolicyType(policy) !== policyTypeFilter) return false;
            if (insurerFilter !== 'all' && (policy.insurerName || '') !== insurerFilter) return false;
            if (isAdmin && ownerFilter !== 'all' && !getOwnerNames(policy).includes(ownerFilter)) return false;
            if (renewalStatusFilter !== 'all' && getRenewalStatus(policy) !== renewalStatusFilter) return false;
            if (statusFilter !== 'all' && policyStatusLabel !== statusFilter && lifecycleStatus !== statusFilter) return false;
            if (!isWithinDateRange(policy)) return false;

            if (!normalizedSearchTerm) return true;

            if (searchColumn === 'all') {
                const valuesToSearch = [
                    getPolicyFieldValue(policy, 'clientName'),
                    getPolicyFieldValue(policy, 'insurerName'),
                    getPolicyFieldValue(policy, 'policyName'),
                    getPolicyFieldValue(policy, 'policyNumber'),
                    getPolicyFieldValue(policy, 'policyStartDate'),
                    getPolicyFieldValue(policy, 'policyEndDate'),
                    getPolicyFieldValue(policy, 'policyType'),
                    getPolicyFieldValue(policy, 'owner'),
                    getPolicyFieldValue(policy, 'renewalStatus'),
                    getPolicyFieldValue(policy, 'grossPremium'),
                    getPolicyFieldValue(policy, 'netPremium'),
                    policyStatusLabel,
                    lifecycleStatus,
                ];

                return valuesToSearch.some((value) =>
                    String(value).toLowerCase().includes(normalizedSearchTerm)
                );
            }

            const fieldValue = getPolicyFieldValue(policy, searchColumn);
            return String(fieldValue).toLowerCase().includes(normalizedSearchTerm);
        });
    }, [
        combinedPolicies,
        getOwnerNames,
        getPolicyFieldValue,
        getPolicyLifecycleStatus,
        getPolicyStatus,
        getPolicyStatusLabel,
        getPolicyType,
        getRenewalStatus,
        insurerFilter,
        isWithinDateRange,
        isAdmin,
        ownerFilter,
        policyTypeFilter,
        renewalStatusFilter,
        searchColumn,
        searchTerm,
        statusFilter,
    ]);

    const getSortableValue = useCallback((policy, columnKey) => {
        if (columnKey === 'policyStartDate' || columnKey === 'policyEndDate') {
            const dateValue = policy[columnKey];
            return dateValue ? new Date(dateValue).getTime() : 0;
        }

        if (columnKey === 'grossPremium' || columnKey === 'netPremium') {
            const num = parseFloat(policy[columnKey]);
            return Number.isNaN(num) ? 0 : num;
        }

        return String(getPolicyFieldValue(policy, columnKey)).toLowerCase();
    }, [getPolicyFieldValue]);

    const sortedPolicies = useMemo(() => [...filteredAndDatedPolicies].sort((a, b) => {
        if (!sortConfig.key) return 0;

        const valueA = getSortableValue(a, sortConfig.key);
        const valueB = getSortableValue(b, sortConfig.key);

        if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    }), [filteredAndDatedPolicies, getSortableValue, sortConfig]);

    const pageLast = currentPage * itemsPerPage;
    const pageFirst = pageLast - itemsPerPage;
    const currentPolicies = sortedPolicies.slice(pageFirst, pageLast);

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchTerm,
        searchColumn,
        dateFrom,
        dateTo,
        policyTypeFilter,
        insurerFilter,
        statusFilter,
        ownerFilter,
        renewalStatusFilter,
        sortConfig,
        itemsPerPage
    ]);

    const summaryCards = useMemo(() => {
        const expiringSoon = combinedPolicies.filter((policy) => {
            const expiry = getExpiryMeta(policy);
            return typeof expiry.days === 'number' && expiry.days >= 0 && expiry.days <= 30;
        }).length;

        return [
            { label: 'Total Policies', value: combinedPolicies.length, icon: FileText, tone: 'blue' },
            { label: 'Active Policies', value: combinedPolicies.filter((policy) => getPolicyLifecycleStatus(policy) === 'Active').length, icon: ShieldCheck, tone: 'green' },
            { label: 'Expiring Soon', value: expiringSoon, icon: RefreshCw, tone: 'amber' },
            { label: 'Renewed Policies', value: combinedPolicies.filter((policy) => getRenewalStatus(policy) === 'Renewed').length, icon: Send, tone: 'violet' },
            { label: 'Pending Renewals', value: combinedPolicies.filter((policy) => getRenewalStatus(policy) === 'Pending').length, icon: Users, tone: 'slate' },
        ];
    }, [combinedPolicies, getExpiryMeta, getPolicyLifecycleStatus, getRenewalStatus]);

    const activeFilterChips = [
        searchTerm && { key: 'search', label: `Search: ${searchTerm}`, onClear: () => setSearchTerm('') },
        policyTypeFilter !== 'all' && { key: 'policyType', label: `Type: ${policyTypeFilter}`, onClear: () => setPolicyTypeFilter('all') },
        insurerFilter !== 'all' && { key: 'insurer', label: `Insurer: ${insurerFilter}`, onClear: () => setInsurerFilter('all') },
        statusFilter !== 'all' && { key: 'status', label: `Status: ${statusFilter}`, onClear: () => setStatusFilter('all') },
        isAdmin && ownerFilter !== 'all' && { key: 'owner', label: `Owner: ${ownerFilter}`, onClear: () => setOwnerFilter('all') },
        renewalStatusFilter !== 'all' && { key: 'renewal', label: `Renewal: ${renewalStatusFilter}`, onClear: () => setRenewalStatusFilter('all') },
        (dateFrom || dateTo) && { key: 'dates', label: `${dateFrom || 'Any'} to ${dateTo || 'Any'}`, onClear: () => { setDateFrom(''); setDateTo(''); } },
    ].filter(Boolean);

    const resetFilters = () => {
        setSearchTerm('');
        setSearchColumn('all');
        setDateFrom('');
        setDateTo('');
        setPolicyTypeFilter('all');
        setInsurerFilter('all');
        setStatusFilter('all');
        setOwnerFilter('all');
        setRenewalStatusFilter('all');
    };

    const toggleRowSelection = (policyId) => {
        setSelectedRows((prev) =>
            prev.includes(policyId)
                ? prev.filter((id) => id !== policyId)
                : [...prev, policyId]
        );
    };

    const toggleAllRows = () => {
        const availableIds = currentPolicies.filter((policy) => !policy.isEndorsement).map((policy) => policy._id);
        const allSelected = availableIds.length > 0 && availableIds.every((id) => selectedRows.includes(id));
        setSelectedRows((prev) => {
            if (allSelected) return prev.filter((id) => !availableIds.includes(id));
            return [...new Set([...prev, ...availableIds])];
        });
    };

    const handleDownloadPolicy = async (policy) => {
        if (!policy?.policyDocPath) {
            alert('Policy document is not uploaded yet.');
            return;
        }
        try {
            const response = await api.get(`${API_BASE_URL}/api/add-policy/document/${policy._id}`, {
                responseType: 'blob'
            });
            saveAs(response.data, policy.policyNumber ? `${policy.policyNumber}-policy-document` : 'policy-document');
        } catch (error) {
            alert(error.response?.data?.error || 'Failed to download policy document.');
        }
    };

    const getRowsForExport = () => {
        if (selectedRows.length > 0) {
            return sortedPolicies.filter((policy) => !policy.isEndorsement && selectedRows.includes(policy._id));
        }

        return sortedPolicies;
    };

    const getExportRows = () =>
        getRowsForExport().map((policy, index) => ({
            'S.No': index + 1,
            Type: policy.isEndorsement ? 'Endorsement' : 'Policy',
            'Client Name': policy.clientName || '',
            Insurer: policy.insurerName || '',
            'Policy Name': policy.policyName || '',
            'Policy Number': policy.policyNumber || '',
            'Policy Type': getPolicyType(policy),
            'Start Date': formatDateForDisplay(policy.policyStartDate) || '',
            'End Date': formatDateForDisplay(policy.policyEndDate) || '',
            'Lifecycle Status': getPolicyLifecycleStatus(policy),
            'Document Status': policy.isEndorsement ? 'Endorsement' : getPolicyStatusLabel(getPolicyStatus(policy)),
            'Assigned User': getAssignedUserName(policy),
            'Renewal Status': getRenewalStatus(policy),
            'Gross Premium': policy.grossPremium ?? '',
            'Net Premium': policy.netPremium ?? ''
        }));

    const handleEmptyExport = () => {
        if (!getRowsForExport().length) {
            alert('No converted corporate policy records available to download.');
            return true;
        }

        return false;
    };

    const exportCorporatePoliciesCsv = () => {
        if (handleEmptyExport()) return;
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'converted-corporate-policies.csv');
        setShowExportMenu(false);
    };

    const exportCorporatePoliciesExcel = () => {
        if (handleEmptyExport()) return;
        const worksheet = XLSX.utils.json_to_sheet(getExportRows());
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Converted Corporate');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), 'converted-corporate-policies.xlsx');
        setShowExportMenu(false);
    };

    const escapeHtml = (value) =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

    const exportCorporatePoliciesPdf = () => {
        if (handleEmptyExport()) return;
        const rows = getExportRows();
        const printWindow = window.open('', '_blank', 'noopener,noreferrer');

        if (!printWindow) {
            alert('Please allow popups to export PDF.');
            return;
        }

        const tableRows = rows.map((row) => `
            <tr>
                <td>${escapeHtml(row['S.No'])}</td>
                <td>${escapeHtml(row.Type)}</td>
                <td>${escapeHtml(row['Client Name'])}</td>
                <td>${escapeHtml(row.Insurer)}</td>
                <td>${escapeHtml(row['Policy Name'])}</td>
                <td>${escapeHtml(row['Policy Number'])}</td>
                <td>${escapeHtml(row['Policy Type'])}</td>
                <td>${escapeHtml(row['End Date'])}</td>
                <td>${escapeHtml(row['Lifecycle Status'])}</td>
                <td>${escapeHtml(row['Document Status'])}</td>
                <td>${escapeHtml(row['Assigned User'])}</td>
                <td>${escapeHtml(row['Gross Premium'])}</td>
            </tr>
        `).join('');

        printWindow.document.write(`
            <!doctype html>
            <html>
              <head>
                <title>Converted Corporate Policies Export</title>
                <style>
                  body { font-family: Arial, sans-serif; color: #111827; margin: 24px; }
                  h1 { margin: 0; font-size: 22px; }
                  p { margin: 6px 0 18px; color: #667085; font-size: 12px; }
                  table { width: 100%; border-collapse: collapse; font-size: 9px; }
                  th, td { border: 1px solid #d7e0ea; padding: 6px; text-align: left; vertical-align: top; }
                  th { background: #f1f5f9; text-transform: uppercase; font-size: 8px; }
                  tr:nth-child(even) td { background: #fbfdff; }
                  @media print { body { margin: 10mm; } }
                </style>
              </head>
              <body>
                <h1>Converted Corporate Policies</h1>
                <p>${rows.length} record(s) exported on ${new Date().toLocaleString('en-IN')}</p>
                <table>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Type</th>
                      <th>Client</th>
                      <th>Insurer</th>
                      <th>Policy</th>
                      <th>Number</th>
                      <th>Policy Type</th>
                      <th>Expiry</th>
                      <th>Status</th>
                      <th>Document</th>
                      <th>Assigned</th>
                      <th>Premium</th>
                    </tr>
                  </thead>
                  <tbody>${tableRows || '<tr><td colspan="12">No converted corporate policy records found.</td></tr>'}</tbody>
                </table>
                <script>
                  window.onload = function () {
                    window.print();
                  };
                </script>
              </body>
            </html>
        `);
        printWindow.document.close();
        setShowExportMenu(false);
    };

    const handleSort = (columnKey) => {
        setSortConfig((prev) => {
            if (prev.key === columnKey) {
                return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key: columnKey, direction: 'asc' };
        });
    };

    const getSortIndicator = (columnKey) => {
        if (sortConfig.key !== columnKey) return '';
        return sortConfig.direction === 'asc' ? '^' : 'v';
    };

    if (!isAuthenticated()) return <Navigate to="/" />;

    return (
        <div className='converted-retail-policies-container'>
            <div className='sidebar'>
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>
            <div className={`converted-retail-policies-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="converted-retail-policies-sticky-header">
                    <Header />
                </div>
                <div className="converted-retail-policies-scroll-area">
                    <section className="policy-table-section">
                        <div className="converted-policy-page-hero">
                            <div>
                                <span className="converted-policy-eyebrow">Transactions / Converted Policy / Corporate</span>
                                <h2>Converted Corporate Policies</h2>
                                <p>{sortedPolicies.length} of {combinedPolicies.length} records visible</p>
                            </div>
                            <button className="converted-policy-reset-btn" type="button" onClick={resetFilters}>
                                Reset filters
                            </button>
                        </div>

                        <div className="converted-policy-summary-grid">
                            {summaryCards.map((card) => {
                                const Icon = card.icon;
                                return (
                                    <article className={`converted-policy-summary-card ${card.tone}`} key={card.label}>
                                        <span className="summary-card-icon"><Icon size={18} /></span>
                                        <div>
                                            <strong>{card.value}</strong>
                                            <span>{card.label}</span>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <div className="converted-policy-filter-panel">
                            <div className="converted-policy-search">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder="Search corporate policies, client, insurer, policy number..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="converted-policy-table-controls">
                                <div className="converted-policy-filter">
                                    <label>Search In</label>
                                    <select value={searchColumn} onChange={(e) => setSearchColumn(e.target.value)}>
                                        <option value="all">All Columns</option>
                                        <option value="clientName">Name</option>
                                        <option value="insurerName">Insurer</option>
                                        <option value="policyName">Policy Name</option>
                                        <option value="policyNumber">Policy Number</option>
                                        <option value="policyType">Policy Type</option>
                                        {isAdmin && <option value="owner">Owner</option>}
                                        <option value="policyEndDate">Expiry Date</option>
                                        <option value="grossPremium">Premium</option>
                                    </select>
                                </div>
                                <div className="converted-policy-filter">
                                    <label>Policy Type</label>
                                    <select value={policyTypeFilter} onChange={(e) => setPolicyTypeFilter(e.target.value)}>
                                        <option value="all">All Types</option>
                                        {filterOptions.policyTypes.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div className="converted-policy-filter">
                                    <label>Insurer</label>
                                    <select value={insurerFilter} onChange={(e) => setInsurerFilter(e.target.value)}>
                                        <option value="all">All Insurers</option>
                                        {filterOptions.insurers.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div className="converted-policy-filter">
                                    <label>Status</label>
                                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                        <option value="all">All Statuses</option>
                                        {statusOptions.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                {isAdmin && (
                                    <div className="converted-policy-filter">
                                        <label>Owner</label>
                                        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
                                            <option value="all">All Owners</option>
                                            {filterOptions.owners.map((option) => <option value={option} key={option}>{option}</option>)}
                                        </select>
                                    </div>
                                )}
                                <div className="converted-policy-filter">
                                    <label>Renewal Status</label>
                                    <select value={renewalStatusFilter} onChange={(e) => setRenewalStatusFilter(e.target.value)}>
                                        <option value="all">All Renewals</option>
                                        {filterOptions.renewalStatuses.map((option) => <option value={option} key={option}>{option}</option>)}
                                    </select>
                                </div>
                                <div className="converted-policy-date-range">
                                    <label>Date Range</label>
                                    <div>
                                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="From date" />
                                        <span className="converted-policy-date-separator">to</span>
                                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="To date" />
                                    </div>
                                </div>
                                {permissions.downloadSelected && <div className="converted-policy-filter-actions">
                                    <div className="converted-policy-export-menu">
                                        <button className="converted-policy-download-btn" type="button" onClick={() => setShowExportMenu((prev) => !prev)}>
                                            <Download size={16} />
                                            Download
                                            <ChevronDown size={15} />
                                        </button>
                                        {showExportMenu && (
                                            <div className="converted-policy-export-dropdown">
                                                <button type="button" onClick={exportCorporatePoliciesCsv}>CSV</button>
                                                <button type="button" onClick={exportCorporatePoliciesExcel}>Excel</button>
                                                <button type="button" onClick={exportCorporatePoliciesPdf}>PDF</button>
                                            </div>
                                        )}
                                    </div>
                                </div>}
                            </div>
                            {activeFilterChips.length > 0 && (
                                <div className="converted-policy-filter-chips">
                                    {activeFilterChips.map((chip) => (
                                        <button type="button" key={chip.key} onClick={chip.onClear}>
                                            {chip.label}
                                            <X size={13} />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {selectedRows.length > 0 && (
                            <div className="converted-policy-bulkbar">
                                <strong>{selectedRows.length} selected</strong>
                                <span>Use row actions for document sharing, downloads, edits, or renewal follow-up.</span>
                            </div>
                        )}

                        {loading ? (
                            <div className="converted-policy-skeleton-list" aria-label="Loading policies">
                                {[1, 2, 3, 4, 5].map((item) => <span key={item} />)}
                            </div>
                        ) : sortedPolicies.length === 0 ? (
                            <div className="converted-policy-empty-state">
                                <FileText size={38} />
                                <h3>No converted corporate policies found</h3>
                                <p>Try clearing filters or widening the date range.</p>
                                <button type="button" onClick={resetFilters}>Clear all filters</button>
                            </div>
                        ) : (
                            <div className="converted-policy-table-scroll">
                                <table className="converted-policy-table">
                                    <thead>
                                        <tr>
                                            <th className="sticky-select-col">
                                                {permissions.downloadSelected && <input
                                                    type="checkbox"
                                                    aria-label="Select all policies"
                                                    checked={currentPolicies.filter((policy) => !policy.isEndorsement).length > 0 && currentPolicies.filter((policy) => !policy.isEndorsement).every((policy) => selectedRows.includes(policy._id))}
                                                    onChange={toggleAllRows}
                                                />}
                                            </th>
                                            <th className="sticky-name-col" onClick={() => handleSort('clientName')} role="button">Name {getSortIndicator('clientName')}</th>
                                            <th onClick={() => handleSort('policyNumber')} role="button">Policy Number {getSortIndicator('policyNumber')}</th>
                                            <th onClick={() => handleSort('policyStartDate')} role="button">Start Date {getSortIndicator('policyStartDate')}</th>
                                            <th onClick={() => handleSort('policyType')} role="button">Policy Type {getSortIndicator('policyType')}</th>
                                            <th onClick={() => handleSort('insurerName')} role="button">Insurer {getSortIndicator('insurerName')}</th>
                                            <th onClick={() => handleSort('grossPremium')} role="button">Premium {getSortIndicator('grossPremium')}</th>
                                            <th onClick={() => handleSort('policyEndDate')} role="button">Expiry Date {getSortIndicator('policyEndDate')}</th>
                                            <th>Status</th>
                                            <th>Assigned User</th>
                                            <th>Renewal</th>
                                            <th className="sticky-actions-col">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentPolicies.map((policy) => {
                                            const lifecycleStatus = getPolicyLifecycleStatus(policy);
                                            const documentStatus = getPolicyStatus(policy);
                                            const expiry = getExpiryMeta(policy);
                                            const assignedUser = getAssignedUserName(policy);
                                            const isSelected = selectedRows.includes(policy._id);

                                            return (
                                                <tr key={policy._id || policy.policyNumber} className={isSelected ? 'is-selected' : ''}>
                                                    <td className="sticky-select-col">
                                                        {!policy.isEndorsement && permissions.downloadSelected && (
                                                            <input
                                                                type="checkbox"
                                                                aria-label={`Select ${policy.clientName || 'policy'}`}
                                                                checked={isSelected}
                                                                onChange={() => toggleRowSelection(policy._id)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td className="sticky-name-col">
                                                        <div className="converted-policy-client-cell">
                                                            <span className="converted-policy-avatar">{getInitials(policy.clientName)}</span>
                                                            <div>
                                                                <strong>{policy.clientName || 'Unnamed Client'}</strong>
                                                                <small>{policy.insurerName || 'No insurer'}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Policy Number">
                                                        {policy.isEndorsement ? (
                                                            <button type="button" className="policy-number-link policy-number-tag-wrap" onClick={() => handleEndorsementView(policy)}>
                                                                <span>{policy.policyNumber}</span>
                                                                <span className="endorsement-tag">END</span>
                                                            </button>
                                                        ) : (
                                                            <button type="button" className="policy-number-link" onClick={() => permissions.view && handlePolicyNumberClick(policy)} disabled={!permissions.view}>
                                                                {policy.policyNumber}
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td data-label="Start Date">{formatDateForDisplay(policy.policyStartDate) || '-'}</td>
                                                    <td data-label="Policy Type"><span className="converted-policy-tag">{getPolicyType(policy)}</span></td>
                                                    <td data-label="Insurer">{policy.insurerName || '-'}</td>
                                                    <td data-label="Premium"><strong className="converted-policy-premium">{formatCurrency(policy.grossPremium)}</strong></td>
                                                    <td data-label="Expiry Date">
                                                        <div className="converted-policy-expiry">
                                                            <strong>{formatDateForDisplay(policy.policyEndDate) || '-'}</strong>
                                                            <span className={`expiry-pill ${expiry.tone}`}>{expiry.label}</span>
                                                        </div>
                                                    </td>
                                                    <td data-label="Status">
                                                        <div className="converted-policy-status-stack">
                                                            <span className={`converted-policy-status-badge ${lifecycleStatus.toLowerCase().replace(/\s+/g, '-')}`}>{lifecycleStatus}</span>
                                                            {policy.isEndorsement ? (
                                                                <span className="converted-policy-doc-label">Endorsement</span>
                                                            ) : (
                                                                <select className={`converted-policy-status-select ${documentStatus}`} value={documentStatus} onChange={(e) => handlePolicyStatusChange(policy, e.target.value)} disabled={!permissions.edit && !permissions.share}>
                                                                    <option value="policy_not_uploaded" disabled={documentStatus !== 'policy_not_uploaded'}>Policy Doc Pending</option>
                                                                    <option value="policy_doc_uploaded" disabled={!permissions.edit}>Policy Doc Uploaded</option>
                                                                    <option value="share_to_client_mail" disabled={!permissions.share || documentStatus === 'policy_not_uploaded'}>Share to Client (Mail)</option>
                                                                    <option value="doc_sent_to_client" disabled>Doc Sent to Client</option>
                                                                </select>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td data-label="Assigned User">
                                                        <div className="converted-policy-assignee">
                                                            <span>{getInitials(assignedUser)}</span>
                                                            <strong>{assignedUser}</strong>
                                                        </div>
                                                    </td>
                                                    <td data-label="Renewal"><span className="converted-policy-renewal-pill">{getRenewalStatus(policy)}</span></td>
                                                    <td className="sticky-actions-col" data-label="Actions">
                                                        <div className="converted-policy-actions">
                                                            {permissions.view && <button type="button" title="View" aria-label="View" onClick={() => policy.isEndorsement ? handleEndorsementView(policy) : handlePolicyNumberClick(policy)}><Eye size={16} /></button>}
                                                            {!policy.isEndorsement && permissions.edit && <button type="button" title="Edit" aria-label="Edit" onClick={() => handleEditClick(policy)}><Edit3 size={16} /></button>}
                                                            {!policy.isEndorsement && permissions.renew && <button type="button" title="Renew" aria-label="Renew" onClick={() => navigate('/renewals')}><RefreshCw size={16} /></button>}
                                                            {!policy.isEndorsement && permissions.downloadDocument && <button type="button" title="Download" aria-label="Download" onClick={() => handleDownloadPolicy(policy)}><Download size={16} /></button>}
                                                            {!policy.isEndorsement && permissions.share && <button type="button" title="Share" aria-label="Share" onClick={() => handlePolicyStatusChange(policy, 'share_to_client_mail')}><Share2 size={16} /></button>}
                                                            {!policy.isEndorsement && permissions.delete && <button type="button" className="danger" title="Delete" aria-label="Delete" onClick={() => handleDeleteClick(policy)}><Trash2 size={16} /></button>}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {sortedPolicies.length > 0 && (
                            <div className="converted-policy-pagination-row">
                                <div className="converted-policy-pagination-meta">
                                    <span>
                                        Showing {pageFirst + 1}-{Math.min(pageLast, sortedPolicies.length)} of {sortedPolicies.length}
                                    </span>
                                    <small>Filtered converted corporate policy records</small>
                                </div>

                                <div className="converted-policy-pagination-controls">
                                    <Pagination
                                        totalItems={sortedPolicies.length}
                                        itemsPerPage={itemsPerPage}
                                        currentPage={currentPage}
                                        onPageChange={setCurrentPage}
                                    />
                                </div>

                                <div className="converted-policy-items-per-page">
                                    <label>Items per page:</label>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </section>
                </div>

                {showPolicyUploadModal && (
                    <div className="converted-policy-modal-overlay">
                        <div className="converted-policy-modal-content">
                            <button
                                className="converted-policy-close-btn"
                                onClick={() => {
                                    setShowPolicyUploadModal(false);
                                    setUploadTargetPolicy(null);
                                    setStatusUploadFile(null);
                                }}
                            >
                                X
                            </button>
                            <h3>Upload Policy Document</h3>
                            <p>
                                {uploadTargetPolicy?.policyNumber
                                    ? `Policy Number: ${uploadTargetPolicy.policyNumber}`
                                    : 'Choose the policy document to continue.'}
                            </p>
                            <PolicyDocumentDropzone
                                file={statusUploadFile}
                                onFileSelect={setStatusUploadFile}
                            />
                            <div className="modal-footer-actions">
                                <button
                                    type="button"
                                    className="converted-policy-save-btn"
                                    onClick={handlePolicyUploadFromStatus}
                                >
                                    Upload
                                </button>
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => {
                                        setShowPolicyUploadModal(false);
                                        setUploadTargetPolicy(null);
                                        setStatusUploadFile(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showEditModal && (
                    <div className="converted-policy-modal-overlay">
                        <div className="converted-policy-modal-content">
                            <button className="converted-policy-close-btn" onClick={() => setShowEditModal(false)}>X</button>
                            <h3>Edit Policy</h3>
                            <form onSubmit={handleEditSave}>
                                <div>
                                    <label>Client Name</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            name="clientName"
                                            value={clientInput}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setClientInput(val);
                                                setEditingPolicy(prev => ({ ...prev, clientName: val }));
                                            }}
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label>Insurer Name</label>
                                    <select
                                        name="insurerName"
                                        value={insurerInput || editingPolicy.insurerName || ''}
                                        onChange={handleInsurerChange}
                                        required
                                    >
                                        <option value="">Select Insurer</option>
                                        {ensureOption(insurerOptions, editingPolicy.insurerName).map((item) => (
                                            <option key={item._id || item.insurerName} value={item.insurerName}>
                                                {item.insurerName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label>Branch Name</label>
                                    <select
                                        name="branchName"
                                        value={editingPolicy.branchName || ''}
                                        onChange={handleEditChange}
                                    >
                                        <option value="">
                                            {editingPolicy.insurerName
                                                ? branches.length === 0
                                                    ? 'No Branch Available'
                                                    : 'Select Branch'
                                                : 'Select Insurer First'}
                                        </option>
                                        {ensureStringOption(branches, editingPolicy.branchName).map((branch) => (
                                            <option key={branch} value={branch}>
                                                {branch}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label>Transaction Date</label>
                                    <input
                                        type="date"
                                        name="transactionDate"
                                        value={editingPolicy.transactionDate || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Payment Mode</label>
                                    <select
                                        type="text"
                                        name="paymentMode"
                                        value={editingPolicy.paymentMode || ''}
                                        onChange={handleEditChange}
                                    >
                                        <option value="" disabled hidden>{editingPolicy.paymentMode || ''}</option>
                                        <option value="Cash">Cash</option>
                                        <option value="UPI">UPI</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="Bank Transfers">Bank Transfers</option>
                                        <option value="Netbanking">Netbanking</option>
                                    </select>
                                </div>

                                <div>
                                    <label>Business Type</label>
                                    <select
                                        name="businessType"
                                        value={editingPolicy.businessType || ''}
                                        onChange={handleEditChange}
                                    >
                                        <option value="">Select Type of Business</option>
                                        <option value="New">New</option>
                                        <option value="Roll Over">I-Pro Renewal</option>
                                        <option value="Renew">Other Renewal</option>
                                    </select>
                                </div>

                                <div className="retail-form-element">
                                    <label className="retail-form-label">Class of Policy</label>
                                    <select
                                        className="retail-select"
                                        name="classOfPolicyId"
                                        value={editingPolicy.classOfPolicyId || ''}
                                        onChange={handleClassOfPolicyChange}
                                        required
                                    >
                                        <option value="">Select Class of Policy</option>
                                        {classOfPolicyOptions.map(item => (
                                            <option key={item._id} value={item._id}>
                                                {item.policyType
                                                    ? `${item.policyType}, ${item.classOfPolicyName}`
                                                    : item.classOfPolicyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="retail-form-element">
                                    <label className="retail-form-label">Policy Name</label>
                                    <select
                                        className="retail-select"
                                        name="policyName"
                                        value={editingPolicy.policyName || ''}
                                        onChange={handlePolicyNameChange}
                                        required
                                    >
                                        <option value="">
                                            {editingPolicy.classOfPolicyId
                                                ? 'Select Policy Name'
                                                : 'Select Class of Policy First'}
                                        </option>
                                        {ensureOption(policyNames.map((name) => ({ policyName: name, _id: name })), editingPolicy.policyName, 'policyName').map((item) => (
                                            <option key={item._id || item.policyName} value={item.policyName}>
                                                {item.policyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label>Policy Number</label>
                                    <input
                                        type="text"
                                        name="policyNumber"
                                        value={editingPolicy.policyNumber || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Policy Issue Date</label>
                                    <input
                                        type="date"
                                        name="policyIssueDate"
                                        value={editingPolicy.policyIssueDate || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Policy Start Date</label>
                                    <input
                                        type="date"
                                        name="policyStartDate"
                                        value={editingPolicy.policyStartDate || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Policy End Date</label>
                                    <input
                                        type="date"
                                        name="policyEndDate"
                                        value={editingPolicy.policyEndDate || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Policy To Be Renewed</label>
                                    <input
                                        type="checkbox"
                                        name="policyToBeRenewed"
                                        checked={editingPolicy.policyToBeRenewed || false}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Gross Premium</label>
                                    <input
                                        type="number"
                                        name="grossPremium"
                                        value={editingPolicy.grossPremium || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>GST Percentage</label>
                                    <input
                                        type="number"
                                        name="gstPercentage"
                                        value={editingPolicy.gstPercentage || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>GST Amount</label>
                                    <input
                                        type="number"
                                        name="gstAmount"
                                        value={editingPolicy.gstAmount || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Net Premium</label>
                                    <input
                                        type="number"
                                        name="netPremium"
                                        value={editingPolicy.netPremium || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Default Commission Percentage</label>
                                    <input
                                        type="number"
                                        name="baseBrokeragePercentage"
                                        value={editingPolicy.baseBrokeragePercentage || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div>
                                    <label>Commission Adjustment</label>
                                    <select
                                        name="commissionAdjustmentType"
                                        value={editingPolicy.commissionAdjustmentType || 'add'}
                                        onChange={handleEditChange}
                                    >
                                        <option value="add">Add</option>
                                        <option value="subtract">Subtract</option>
                                    </select>
                                </div>

                                <div>
                                    <label>Adjustment Percentage</label>
                                    <input
                                        type="number"
                                        name="commissionAdjustmentPercentage"
                                        value={editingPolicy.commissionAdjustmentPercentage || ''}
                                        onChange={handleEditChange}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>

                                <div>
                                    <label>Final Commission Percentage</label>
                                    <input
                                        type="number"
                                        name="brokeragePercentage"
                                        value={editingPolicy.brokeragePercentage || ''}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                {isAdmin && (
                                    <div>
                                        <label>Total Brokerage</label>
                                        <input
                                            type="number"
                                            name="totalBrokerage"
                                            value={editingPolicy.totalBrokerage || ''}
                                            onChange={handleEditChange}
                                        />
                                    </div>
                                )}

                                <div>
                                    <label>Policy Doc</label>
                                    {editingPolicy.policyDocPath ? permissions.downloadDocument ? (
                                        <button type="button" onClick={() => handleDownloadPolicy(editingPolicy)}>
                                            Download Document
                                        </button>
                                    ) : (
                                        <p style={{ fontStyle: 'italic', color: '#999' }}>Document download restricted</p>
                                    ) : (
                                        <p style={{ fontStyle: 'italic', color: '#999' }}>No document attached</p>
                                    )}
                                </div>
                                <br></br>


                                <div>
                                    <label>Reattach Policy Doc</label>
                                    <input
                                        type="file"
                                        name="policyDoc"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => setSelectedFile(e.target.files[0])}
                                    />
                                </div>


                                <div className="modal-footer-actions">
                                    <button type="submit" className="converted-policy-save-btn">
                                        Save Changes
                                    </button>

                                    <button
                                        type="button"
                                        className="cancel-btn"
                                        onClick={() => setShowEditModal(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showViewModal && (
                    <div className="converted-policy-modal-overlay">
                        <div className="converted-policy-modal-content">
                            <button className="converted-policy-close-btn" onClick={() => setShowViewModal(false)}>X</button>
                            <h3>Policy Details</h3>
                            <form onSubmit={(e) => e.preventDefault()}>
                                <div>
                                    <label>Client Name</label>
                                    <input type="text" value={viewingPolicy.clientName || ''} readOnly />
                                </div>
                                <div>
                                    <label>Insurer Name</label>
                                    <input type="text" value={viewingPolicy.insurerName || ''} readOnly />
                                </div>
                                <div>
                                    <label>Branch Name</label>
                                    <input type="text" value={viewingPolicy.branchName || ''} readOnly />
                                </div>
                                <div>
                                    <label>Transaction Date</label>
                                    <input type="date" value={viewingPolicy.transactionDate || ''} readOnly />
                                </div>
                                <div>
                                    <label>Payment Mode</label>
                                    <input type="text" value={viewingPolicy.paymentMode || ''} readOnly />
                                </div>
                                <div>
                                    <label>Business Type</label>
                                    <input type="text" value={viewingPolicy.businessType || ''} readOnly />
                                </div>
                                <div className="retail-form-element">
                                    <label className="retail-form-label">Policy Name</label>
                                    <input type="text" value={viewingPolicy.policyName || ''} readOnly />
                                </div>
                                <div>
                                    <label>Policy Number</label>
                                    <input type="text" value={viewingPolicy.policyNumber || ''} readOnly />
                                </div>
                                <div>
                                    <label>Policy Issue Date</label>
                                    <input type="date" value={viewingPolicy.policyIssueDate || ''} readOnly />
                                </div>
                                <div>
                                    <label>Policy Start Date</label>
                                    <input type="date" value={viewingPolicy.policyStartDate || ''} readOnly />
                                </div>
                                <div>
                                    <label>Policy End Date</label>
                                    <input type="date" value={viewingPolicy.policyEndDate || ''} readOnly />
                                </div>
                                <div>
                                    <label>Policy To Be Renewed</label>
                                    <input type="checkbox" checked={viewingPolicy.policyToBeRenewed || false} disabled />
                                </div>
                                <div>
                                    <label>Gross Premium</label>
                                    <input type="number" value={viewingPolicy.grossPremium || ''} readOnly />
                                </div>
                                <div>
                                    <label>GST Percentage</label>
                                    <input type="number" value={viewingPolicy.gstPercentage || ''} readOnly />
                                </div>
                                <div>
                                    <label>GST Amount</label>
                                    <input type="number" value={viewingPolicy.gstAmount || ''} readOnly />
                                </div>
                                <div>
                                    <label>Net Premium</label>
                                    <input type="number" value={viewingPolicy.netPremium || ''} readOnly />
                                </div>
                                {isAdmin && (
                                    <>
                                        <div>
                                            <label>Brokerage Percentage</label>
                                            <input type="number" value={viewingPolicy.brokeragePercentage || ''} readOnly />
                                        </div>
                                        <div>
                                            <label>Total Brokerage</label>
                                            <input type="number" value={viewingPolicy.totalBrokerage || ''} readOnly />
                                        </div>
                                    </>
                                )}
                                <div>
                                    <label>Policy Doc</label>
                                    {viewingPolicy.policyDocPath ? permissions.downloadDocument ? (
                                        <button type="button" onClick={() => handleDownloadPolicy(viewingPolicy)}>
                                            Download Document
                                        </button>
                                    ) : (
                                        <p style={{ fontStyle: 'italic', color: '#999' }}>Document download restricted</p>
                                    ) : (
                                        <p style={{ fontStyle: 'italic', color: '#999' }}>No document attached</p>
                                    )}
                                </div>
                                <div className="modal-footer-actions">
                                    <button
                                        type="button"
                                        className="cancel-btn"
                                        onClick={() => setShowViewModal(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showEndorsementViewModal && (
                    <div className="converted-policy-modal-overlay">
                        <div className="converted-policy-modal-content">
                            <button className="converted-policy-close-btn" onClick={() => setShowEndorsementViewModal(false)}>X</button>
                            <h3>Endorsement Details</h3>
                            <form onSubmit={(e) => e.preventDefault()}>
                                <div>
                                    <label>Client Name</label>
                                    <input type="text" value={viewingEndorsement.clientName || ''} readOnly />
                                </div>
                                <div>
                                    <label>Insurer Name</label>
                                    <input type="text" value={viewingEndorsement.insurerName || ''} readOnly />
                                </div>
                                <div>
                                    <label>Policy Name</label>
                                    <input type="text" value={viewingEndorsement.policyName || ''} readOnly />
                                </div>
                                <div>
                                    <label>Policy Number</label>
                                    <input type="text" value={viewingEndorsement.policyNumber || ''} readOnly />
                                </div>
                                <div>
                                    <label>Policy Start Date</label>
                                    <input type="date" value={formatDateForInput(viewingEndorsement.policyStartDate)} readOnly />
                                </div>
                                <div>
                                    <label>Policy End Date</label>
                                    <input type="date" value={formatDateForInput(viewingEndorsement.policyEndDate)} readOnly />
                                </div>
                                <div>
                                    <label>Endorsement Number</label>
                                    <input type="text" value={viewingEndorsement.endorsementNumber || ''} readOnly />
                                </div>
                                <div>
                                    <label>Endorsement Date</label>
                                    <input type="date" value={formatDateForInput(viewingEndorsement.endorsementDate)} readOnly />
                                </div>
                                <div>
                                    <label>Endorsement Type</label>
                                    <input type="text" value={viewingEndorsement.endorsementType || ''} readOnly />
                                </div>
                                <div>
                                    <label>Gross Premium</label>
                                    <input type="number" value={viewingEndorsement.additionalGrossPremium ?? ''} readOnly />
                                </div>
                                <div>
                                    <label>Net Premium</label>
                                    <input type="number" value={viewingEndorsement.additionalNetPremium ?? ''} readOnly />
                                </div>
                                <div>
                                    <label>Status</label>
                                    <input type="text" value={viewingEndorsement.status || ''} readOnly />
                                </div>
                                <div>
                                    <label>Remarks</label>
                                    <input type="text" value={viewingEndorsement.remarks || ''} readOnly />
                                </div>
                                <div>
                                    <label>Endorsement Doc</label>
                                    {viewingEndorsement.endorsementDocPath ? (
                                        <a
                                            href={`${API_BASE_URL}/${viewingEndorsement.endorsementDocPath}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ display: 'inline-block', marginTop: '8px', color: '#007bff', textDecoration: 'underline' }}
                                        >
                                            View Document
                                        </a>
                                    ) : (
                                        <p style={{ fontStyle: 'italic', color: '#999' }}>No document attached</p>
                                    )}
                                </div>
                                <div className="modal-footer-actions">
                                    <button
                                        type="button"
                                        className="cancel-btn"
                                        onClick={() => setShowEndorsementViewModal(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showDeleteConfirmationModal && (
                    <div className="converted-policy-confirmation-modal-overlay">
                        <div className="confirmation-modal-content">
                            <button className="close-btn" onClick={handleCloseDeleteConfirmationModal}>X</button>
                            <h3>Confirm Deletion</h3>
                            <p>Please type the policy number to confirm deletion:</p>
                            <input
                                type="text"
                                value={confirmationId}
                                onChange={(e) => setConfirmationId(e.target.value)}
                                placeholder="Enter policy number"
                            />
                            <div className="confirmation-modal-actions">
                                <button className="confirm-btn" onClick={handleDelete}>Confirm Deletion</button>
                                <button className="cancel-btn" onClick={handleCloseDeleteConfirmationModal}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConvertedCorporatePolicy;
